import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { RoutePath } from '@components/AppRouter/constants';
import { cmApi } from '@src/api';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';
import type { TableComponentRequestDto } from '@src/api';

import { CopySettingsModal } from '../../../CopySettingsModal';
import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const [dataReportVersionCodes, setDataReportVersionCodes] = useState<string[]>([]);

  const { reportVersions } = reportVersionsStore;
  const {
    breadcrumbs,
    selectNode,
    toggleCopySettingsModalVisible,
    isCopySettingsModalVisible,
    reportSettings,
    checked,
    setChecked,
  } = reportSettingsStore;

  const { push } = useHistory();

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_REPORT_SETTINGS });

  useEffect(() => {
    update();
  }, [selectNode, update]);

  useEffect(() => {
    fetchReferences(['reportVersions']);
  }, []);

  const hardFilter = useMemo(() => {
    if (selectNode) {
      const out = [
        {
          name: 'organization_code',
          type: FILTER.EQ,
          values: [selectNode.organizationCode ?? ''],
          filterIsInverted: false,
        },
      ];

      if (selectNode.reportGroupCode) {
        out.push({
          name: 'report_group_code',
          type: FILTER.EQ,
          values: [selectNode.reportGroupCode],
          filterIsInverted: false,
        });
      }

      if (selectNode.reportTypeCode) {
        out.push({
          name: 'report_type_code',
          type: FILTER.EQ,
          values: [selectNode.reportTypeCode],
          filterIsInverted: false,
        });
      }

      return [out];
    }

    return undefined;
  }, [selectNode]);

  useEffect(() => {
    if (selectNode) {
      const tableInfo = {
        aggregate: {},
        fields: ['report_version_code'],
        filter: [],
        groupBy: [],
        hardFilter: hardFilter ?? [],
        limit: 1000,
        offset: 0,
        openGroup: [],
        orderBy: [],
        resource: 'v_report_settings_tc',
      };

      cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
        const result = response?.data as unknown as { data: { report_version_code: string }[] };
        if (result && result.data && result.data.length > 0) {
          setDataReportVersionCodes(result.data.map(({ report_version_code }) => report_version_code));
        }
      });
    }
  }, [hardFilter, selectNode]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr, hardFilter },
        localData: {
          data: reportSettings.map((item) => objectFieldsToSnakeCase(item)),
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'report_version_code',
      });

      setChecked(rows);
    },
    [hardFilter, reportSettings, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_REPORT_SETTINGS}/${value?.report_version_code}`);
    },
    [push],
  );

  const availableVersions = useMemo(() => {
    const repVerCode = checked[0]?.report_version_code;
    const reportCode = reportVersions.find((ver) => ver.code === repVerCode)?.reportCode;
    return reportVersions
      .filter(
        (ver) => ver.reportCode === reportCode && ver.code !== repVerCode && !dataReportVersionCodes.includes(ver.code),
      )
      .map((repVer) => ({ id: repVer.id, name: repVer.id + '-' + repVer.name }));
  }, [checked, dataReportVersionCodes, reportVersions]);

  const title = useMemo(
    () => (
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    ),
    [breadcrumbs],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  if (!selectNode) return null;

  return (
    <div className={styles.table}>
      <>
        <KSNMTable
          externalControl={externalControl}
          tableName={TABLE_NAMES.DICTIONARY_REPORT_SETTINGS}
          columns={columns}
          tableTitle={titleWithCount}
          resource="v_report_settings_tc"
          externalFetch={ksnmTableStore.externalFetch}
          externalFetchXLSX={getExcelWithAudit('Справочник "Настройка отчета"')}
          hardFilter={hardFilter}
          onRowClick={onRowClick}
          onCheckBoxChange={onCheckBoxChange}
          extendTableButtons={<ActionButton update={update} availableVersions={availableVersions} />}
          showAggregateRow={false}
          onTableChange={onTableChange}
          key={key}
          keyField="report_version_code"
        />

        <CopySettingsModal
          versions={availableVersions}
          visible={isCopySettingsModalVisible}
          toggleVisibility={toggleCopySettingsModalVisible}
          update={update}
        />
      </>
    </div>
  );
});
